import React, { useLayoutEffect, useState } from "react";
import { RetweetOutlined, DeleteOutlined } from "@ant-design/icons";
import {
  Badge,
  Avatar,
  Col,
  Row,
  Spin,
  Button,
  Input,
  Form,
  Layout,
  Card,
  ColorPicker,
} from "antd";
import { displayMessage, postAPI, getAPI } from "../../utils/common";
import { ERROR_MSG_TYPE, SUCCESS_MESSAGE } from "../../constants/messages";
import { CREATE_SETTING, SETTING, IMAGE_BASE_URL } from "../../constants/api";
import LayoutHeader from "../inc/LayoutHeader";
import LayoutFooter from "../inc/LayoutFooter";
import ImageSelect from "../images/ImageSelect";
import LS_SERVICE from "../../utils/localStorage";
import { THEME } from "../../constants/constent";

const Setting = () => {
  const { Content } = Layout;
  const [form] = Form.useForm();
  const [loading, setIsLoading] = useState(true);

  // State for Image selection
  const [selectedLogo, setSelectedLogo] = useState("");
  const [selectedLogoValue, setSelectedLogoValue] = useState(null);
  const [isLogoDialog, setIsLogoDialog] = useState(false);

  const [selectedFavicon, setSelectedFavicon] = useState("");
  const [selectedFaviconValue, setSelectedFaviconValue] = useState(null);
  const [isFaviconDialog, setIsFaviconDialog] = useState(false);
  const [color, setColor] = useState(THEME);

  const handleColorChange = (value) => {
    const selectedColor = value.toHex();
    setColor(selectedColor);
    console.log("Selected color:", selectedColor);
  };
  // Image Selection Handlers
  const openLogoDialog = () => setIsLogoDialog(true);
  const closeLogoDialog = () => setIsLogoDialog(false);
  const openFaviconDialog = () => setIsFaviconDialog(true);
  const closeFaviconDialog = () => setIsFaviconDialog(false);

  const handleLogoDialogOk = (selectedImg) =>
    handleImageSelection(selectedImg, "logo");
  const handleFaviconDialogOk = (selectedImg) =>
    handleImageSelection(selectedImg, "favicon");

  const handleImageSelection = (selectedImg, type) => {
    if (selectedImg.length > 0) {
      const imagePath = selectedImg[0].filePath;
      if (type === "logo") {
        setSelectedLogo(selectedImg[0].id);
        setSelectedLogoValue(imagePath);
        form.setFieldsValue({ company_logo: imagePath });
      } else if (type === "favicon") {
        setSelectedFavicon(selectedImg[0].id);
        setSelectedFaviconValue(imagePath);
        form.setFieldsValue({ company_favicon: imagePath });
      }
    } else {
      displayMessage(ERROR_MSG_TYPE, "Image not selected");
    }
  };

  // Remove the Selected Image
  const removeSelectedImage = (type) => {
    if (type === "logo") {
      setSelectedLogo("");
      setSelectedLogoValue(null);
      form.setFieldsValue({ company_logo: "" });
    } else if (type === "favicon") {
      setSelectedFavicon("");
      setSelectedFaviconValue(null);
      form.setFieldsValue({ company_favicon: "" });
    }
  };

  useLayoutEffect(() => {
    getSetting();
  }, []);

  const getSetting = () => {
    getAPI(SETTING, handleSettingSuccess, handleSettingError);
  };

  const handleSettingSuccess = (data) => {
    if (data.status) {
      const setting = data.data;
      const ls_settings = {
        logo: setting.company_logo.filePath || null,
        favicon: setting.company_favicon.filePath || null,
        company_name: setting.company_name || null,
        company_address: setting.company_address || null,
        company_email: setting.company_email || null,
        company_description: setting.company_description || null,
        company_phone: setting.company_phone || null,
        theme: setting.theme || THEME,
      };
      if (LS_SERVICE.has(setting)) {
        LS_SERVICE.edit("setting", ls_settings);
      } else {
        LS_SERVICE.set("setting", ls_settings);
      }
      document.documentElement.style.setProperty(
        "--primary-color",
        "#" + ls_settings.theme
      );
      form.setFieldsValue({
        company_name: setting.company_name,
        company_description: setting.company_description,
        company_phone: setting.company_phone,
        company_email: setting.company_email,
        company_address: setting.company_address,
        company_gst: setting.company_gst,
        company_pancard: setting.company_pancard,
        company_fssai: setting.company_fssai,
        minimum_order_amount: setting.minimum_order_amount,
        free_delivery_on: setting.free_delivery_on,
        delivery_charge: setting.delivery_charge,
        plateform_charge: setting.plateform_charge,
        order_series: setting.order_series,
        invoice_series: setting.invoice_series,
        payment_secrit_key: setting.payment_secrit_key,
        payment_app_id: setting.payment_app_id,
        online_accept: setting.online_accept,
        cod_accept: setting.cod_accept,
        delivery_by: setting.delivery_by,
        delivery_email_id: setting.delivery_email_id,
        delivery_password: setting.delivery_password,
        cancel_enable: setting.cancel_enable,
        cancel_enable_day: setting.cancel_enable_day,
        theme: setting.theme,
      });
      setColor(setting.theme);

      if (setting.company_logo) {
        setSelectedLogo(setting.company_logo.id);
        setSelectedLogoValue(setting.company_logo.filePath);
        form.setFieldsValue({ company_logo: setting.company_logo.filePath });
      }

      if (setting.company_favicon) {
        setSelectedFavicon(setting.company_favicon.id);
        setSelectedFaviconValue(setting.company_favicon.filePath);
        form.setFieldsValue({
          company_favicon: setting.company_favicon.filePath,
        });
      }
    } else {
      displayMessage(ERROR_MSG_TYPE, data.message);
    }
    setIsLoading(false);
  };

  const handleSettingError = () => {
    setIsLoading(false);
    displayMessage(ERROR_MSG_TYPE, "Unable to Get Detail");
  };

  const createSetting = (values) => {
    setIsLoading(true);
    const body = values;
    if (selectedLogo) body.company_logo = selectedLogo;
    if (selectedFavicon) body.company_favicon = selectedFavicon;
    body.theme = color;
    console.log("createSetting", body);

    postAPI(CREATE_SETTING, body, success, error);
  };

  const success = (data) => {
    if (data.status) {
      getSetting();
      displayMessage(SUCCESS_MESSAGE, data.message);
    } else {
      displayMessage(ERROR_MSG_TYPE, data.message);
    }
    setIsLoading(false);
  };

  const error = () => {
    setIsLoading(false);
    displayMessage(ERROR_MSG_TYPE, "Error while saving settings");
  };

  return (
    <Layout>
      <LayoutHeader />
      <Content>
        <Card
          title="Setting"
          bordered={true}
          extra={
            <Button
              style={{ marginTop: "5px", marginBottom: "5px" }}
              onClick={getSetting}
              type="primary"
              ghost
            >
              <RetweetOutlined />
            </Button>
          }
        >
          <Spin spinning={loading}>
            <Form onFinish={createSetting} form={form} layout="vertical">
              <Form.Item style={{ marginBottom: 0 }}>
                <Row gutter={[16, 16]}>
                  {/* Company Logo */}
                  <Col span={6}>
                    <Form.Item
                      name="company_logo"
                      label={
                        <span>
                          Company Logo{" "}
                          <span style={{ color: "red", fontWeight: "bold" }}>
                            *Pref Size (1550x358)
                          </span>
                        </span>
                      }
                    >
                      <Row>
                        <Col span={selectedLogo === "" ? 24 : 15}>
                          <Input
                            type="text"
                            readOnly
                            onClick={openLogoDialog}
                            value={
                              selectedLogo === ""
                                ? "Click to select image"
                                : selectedLogoValue
                            }
                            placeholder="Company Logo *Pref Size (1550x358)"
                          />
                        </Col>
                        {selectedLogo !== "" && (
                          <>
                            <Col span={3}>
                              <Badge>
                                <Avatar
                                  src={IMAGE_BASE_URL + selectedLogoValue}
                                  shape="circle"
                                  size="default"
                                />
                              </Badge>
                            </Col>
                            <Col span={3}>
                              <DeleteOutlined
                                onClick={() => removeSelectedImage("logo")}
                              />
                            </Col>
                          </>
                        )}
                      </Row>
                    </Form.Item>
                  </Col>
                  {/* Company Favicon */}
                  <Col span={6}>
                    <Form.Item
                      name="company_favicon"
                      label={
                        <span>
                          Company Favicon{" "}
                          <span style={{ color: "red", fontWeight: "bold" }}>
                            *Pref Size (380x411)
                          </span>
                        </span>
                      }
                    >
                      <Row>
                        <Col span={selectedFavicon === "" ? 24 : 15}>
                          <Input
                            type="text"
                            readOnly
                            onClick={openFaviconDialog}
                            value={
                              selectedFavicon === ""
                                ? "Click to select image"
                                : selectedFaviconValue
                            }
                            placeholder="Company Favicon"
                          />
                        </Col>
                        {selectedFavicon !== "" && (
                          <>
                            <Col span={3}>
                              <Badge>
                                <Avatar
                                  src={IMAGE_BASE_URL + selectedFaviconValue}
                                  shape="circle"
                                  size="default"
                                />
                              </Badge>
                            </Col>
                            <Col span={3}>
                              <DeleteOutlined
                                onClick={() => removeSelectedImage("favicon")}
                              />
                            </Col>
                          </>
                        )}
                      </Row>
                    </Form.Item>
                  </Col>
                  {renderFormFields()}
                  <Col span={6}>
                    <Form.Item name="theme" label="Theme">
                      <ColorPicker
                        defaultValue={color}
                        showText
                        allowClear
                        onChange={handleColorChange}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form.Item>
              <Row>
                <Col span={24}>
                  <Form.Item style={{ float: "right" }} label="" colon={false}>
                    <Button loading={loading} block htmlType="submit">
                      Save
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Spin>
        </Card>
      </Content>
      <LayoutFooter />
      <ImageSelect
        key="logo"
        isOpen={isLogoDialog}
        onClose={closeLogoDialog}
        onOk={handleLogoDialogOk}
        fromMager={false}
      />
      <ImageSelect
        key="favicon"
        isOpen={isFaviconDialog}
        onClose={closeFaviconDialog}
        onOk={handleFaviconDialogOk}
        fromMager={false}
      />
    </Layout>
  );
};

const renderFormFields = () => {
  const formFields = [
    {
      name: "company_name",
      label: "Company Name",
      placeholder: "Company Name",
      required: true,
      regex: /^[a-zA-Z0-9\s,.'-]{3,}$/, // Example regex for name (alphanumeric with spaces)
      errorMessage:
        "Company name must be at least 3 characters long and alphanumeric.",
    },
    {
      name: "company_description",
      label: "Company Description",
      placeholder: "Company Description",
      required: true,
    },
    {
      name: "company_phone",
      label: "Company Phone",
      placeholder: "Company Phone",
      required: true,
      regex: /^[0-9]{10}$/, // Regex for 10-digit phone number
      errorMessage: "Please enter a valid 10-digit phone number.",
    },
    {
      name: "company_email",
      label: "Company Email",
      placeholder: "Company Email",
      required: true,
      regex: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, // Regex for email
      errorMessage: "Please enter a valid email address.",
    },
    {
      name: "company_address",
      label: "Company Address",
      placeholder: "Company Address",
      required: true,
    },
    {
      name: "company_gst",
      label: "Company GST NO.",
      placeholder: "Company GST",
      required: true,
      regex: /\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}[Z]{1}[A-Z\d]{1}/, // Regex for GST (e.g., 27AAACS1234F1Z5)
      errorMessage: "Please enter a valid 15-digit GST number.",
    },
    {
      name: "company_pancard",
      label: "Company Pan-Card",
      placeholder: "Company Pan-Card",
      required: true,
      regex: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, // Regex for Pan-Card (e.g., AAAAA1111A)
      errorMessage: "Please enter a valid PAN card number.",
    },
    {
      name: "company_fssai",
      label: "Company FSSAI",
      placeholder: "Company FSSAI",
      required: true,
    },
    {
      name: "minimum_order_amount",
      label: "Minimum Order Amt",
      placeholder: "Minimum Order Amount",
      required: true,
      regex: /^[0-9]+(\.[0-9]{1,2})?$/, // Regex for price (allow decimals)
      errorMessage: "Please enter a valid minimum order amount.",
    },
    {
      name: "free_delivery_on",
      label: "Delivery Charge Free On",
      placeholder: "Delivery Charge Free On",
      required: true,
      regex: /^[0-9]+(\.[0-9]{1,2})?$/, // Regex for alphabetic free delivery location
      errorMessage: "Please enter a valid location for free delivery.",
    },
    {
      name: "delivery_charge",
      label: "Delivery Charge",
      placeholder: "Delivery Charge",
      required: true,
      regex: /^[0-9]+(\.[0-9]{1,2})?$/, // Regex for decimal charges
      errorMessage: "Please enter a valid delivery charge.",
    },
    {
      name: "delivery_by",
      label: "Delivery By",
      placeholder: "Delivery By",
      required: true,
      regex: /^(Self|Shiprocket)$/, // Regex for "Self" or "Shiprocket"
      errorMessage: "Please enter a valid delivery by (Self or Shiprocket).",
    },
    {
      name: "plateform_charge",
      label: "Plateform Charge",
      placeholder: "Plateform Charge",
      required: true,
      regex: /^[0-9]+(\.[0-9]{1,2})?$/, // Regex for decimal charges
      errorMessage: "Please enter a valid platform charge.",
    },
    {
      name: "order_series",
      label: "Order Series",
      placeholder: "Order Series",
      required: true,
    },
    {
      name: "invoice_series",
      label: "Invoice Series",
      placeholder: "Invoice Series",
      required: true,
    },
    {
      name: "payment_secrit_key",
      label: "Razorpay Key-ID",
      placeholder: "Razorpay Key-ID",
      required: true,
    },
    {
      name: "payment_app_id",
      label: "Razorpay Key-Secret",
      placeholder: "Razorpay Key-Secret",
      required: true,
    },
    {
      name: "online_accept",
      label: "Razorpay ON/OFF  (0:ON, 1:OFF)",
      placeholder: "Razorpay Status",
      required: true,
      regex: /^(0|1)$/, // Regex for "0" or "1"
      errorMessage: "Please enter a valid Razorpay accept(0 or 1).",
    },
    {
      name: "cod_accept",
      label: "COD ON/OFF  (0:ON, 1:OFF)",
      placeholder: "COD Status",
      required: true,
      regex: /^(0|1)$/, // Regex for "0" or "1"
      errorMessage: "Please enter a valid COD accept(0 or 1).",
    },

    {
      name: "delivery_email_id",
      label: "Delivery Email",
      placeholder: "Delivery Email",
      required: true,
      regex: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, // Regex for email
      errorMessage: "Please enter a valid email address.",
    },
    {
      name: "delivery_password",
      label: "Delivery Password",
      placeholder: "Delivery Password",
      required: true,
    },
    {
      name: "cancel_enable",
      label: "Cancel ON/OFF  (0:ON, 1:OFF)",
      placeholder: "Cancel Status",
      required: true,
      regex: /^(0|1)$/, // Regex for "0" or "1"
      errorMessage: "Please enter a valid Cancel accept(0 or 1).",
    },
    {
      name: "cancel_enable_day",
      label: "Cancel Day ON/OFF  (0:ON, 1:OFF)",
      placeholder: "Cancel Day Status",
      required: true,
      regex: /^(0|1)$/, // Regex for "0" or "1"
      errorMessage: "Please enter a valid Cancel Day accept(0 or 1).",
    },
  ];

  return formFields.map((field, index) => (
    <Col key={index} span={6}>
      <Form.Item
        name={field.name}
        label={field.label}
        rules={[
          {
            required: field.required,
            message: `${field.label} is required!`,
          },
          field.regex && {
            pattern: field.regex,
            message: field.errorMessage,
          },
        ]}
      >
        <Input type="text" placeholder={field.placeholder} />
      </Form.Item>
    </Col>
  ));
};

export default Setting;
